// src/pages/tender/DailyProgressPage.js
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";
import dayjs from "dayjs";

const cx = (...xs) => xs.filter(Boolean).join(" ");

// 🔹 Timestamp / {seconds} / Date → safe Date
const safeDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === "function") return v.toDate();
  if (v instanceof Date) return v;
  if (typeof v.seconds === "number") return new Date(v.seconds * 1000);
  return null;
};

// 🔹 Construction stages
const STAGES = [
  { key: "EXCAVATION", label: "Excavation / Site Work" },
  { key: "FOOTING", label: "Footing" },
  { key: "PLINTH_BEAM", label: "Plinth Beam" },
  { key: "COLUMNS_BEAMS", label: "Column & Beam Work" },
  { key: "BRICK_MASONRY", label: "Brick Masonry Work" },
  { key: "PLASTER", label: "Plaster Work" },
  { key: "PEB_INSPECTION", label: "PEB Inspection" },
  { key: "PEB_INSTALLATION", label: "PEB Installation" },
  { key: "COLOUR_WORK", label: "Colour Work" },
  { key: "FINISHING", label: "Finishing" },
  { key: "COMPLETED", label: "Completed" },
];

function Badge({ color = "gray", children }) {
  const map = {
    green: "bg-emerald-100 text-emerald-700",
    yellow: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
    gray: "bg-slate-100 text-slate-700",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold",
        map[color] || map.gray
      )}
    >
      {children}
    </span>
  );
}

export default function DailyProgressPage() {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [tender, setTender] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔹 NEW: BOQ items list + selected BOQ item
  const [boqItems, setBoqItems] = useState([]);
  const [selectedBoqId, setSelectedBoqId] = useState("");

  // form states
  const [stageKey, setStageKey] = useState("EXCAVATION");
  const [qtyToday, setQtyToday] = useState("");
  const [remarks, setRemarks] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);

  /* ---------------- AUTH ---------------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) {
        toast.error("Please login first.");
        navigate("/tender/login");
      } else {
        setUser(u);
      }
    });
    return () => unsub();
  }, [auth, navigate]);

  /* ---------------- LOAD VENDOR, TENDER, BOQ, LOGS ---------------- */
  useEffect(() => {
    if (!user) return;

    const load = async () => {
      try {
        setLoading(true);

        // Vendor
        const vSnap = await getDoc(doc(db, "vendors", user.uid));
        setVendor(vSnap.data() || null);

        // Tender
        const tSnap = await getDoc(doc(db, "tenders", tenderId));
        if (!tSnap.exists()) {
          toast.error("Tender not found.");
          navigate("/tender/contractor", { replace: true });
          return;
        }
        const t = { id: tSnap.id, ...tSnap.data() };
        if (t.awardedVendorUid && t.awardedVendorUid !== user.uid) {
          toast.error("This project is not assigned to your company.");
          navigate("/tender/contractor", { replace: true });
          return;
        }
        setTender(t);

        // 🔹 NEW: BOQ items (for dropdown)
        const boqSnap = await getDocs(
          collection(db, "tenders", tenderId, "boq_items")
        );
        const list = [];
        boqSnap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.itemNo || 0) - (b.itemNo || 0));
        setBoqItems(list);
        if (!selectedBoqId && list.length) {
          setSelectedBoqId(list[0].id);
        }

        // Logs
        const logsSnap = await getDocs(
          collection(db, "tenders", tenderId, "construction_logs")
        );
        const arr = [];
        logsSnap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
        arr.sort(
          (a, b) =>
            (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
        );
        setLogs(arr);
      } catch (err) {
        console.error(err);
        toast.error("Error loading daily progress.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user, tenderId, navigate]);

  /* ---------------- SAVE ENTRY ---------------- */
  const handleSave = async () => {
    if (!qtyToday) {
      toast.error("Please enter quantity.");
      return;
    }
    if (!selectedBoqId) {
      toast.error("Please select BOQ Item.");
      return;
    }

    const selectedBoq =
      boqItems.find((b) => b.id === selectedBoqId) || {};

    try {
      setSaving(true);

      const colRef = collection(db, "tenders", tenderId, "construction_logs");
      const logRef = doc(colRef);

      // Upload photos
      const uploaded = [];
      for (const f of photos) {
        const path = `progress/${tenderId}/${logRef.id}/${Date.now()}_${f.name}`;
        const sRef = ref(storage, path);
        await uploadBytes(sRef, f);
        const url = await getDownloadURL(sRef);
        uploaded.push({ url, name: f.name });
      }

      const payload = {
        logId: logRef.id,
        tenderId,
        vendorUid: user.uid,
        vendorName: vendor?.profile?.companyName || "",
        date: new Date(),
        stageKey,
        stageStatus: "IN_PROGRESS", // contractor side hamesha IN_PROGRESS

        // 🔹 NEW: link with BOQ item
        boqId: selectedBoqId,
        boqItemNo: selectedBoq.itemNo || null,
        boqDescription: selectedBoq.description || "",

        remarks,
        qtyToday: Number(qtyToday),
        photos: uploaded,
        pmcStatus: "PENDING", // PMC ke liye pending
        pmcRemark: "",
        createdAt: serverTimestamp(),
      };

      await setDoc(logRef, payload);

      setLogs((prev) => [{ ...payload, id: logRef.id }, ...prev]);

      toast.success("Progress submitted (Pending for PMC).");
      setQtyToday("");
      setRemarks("");
      setPhotos([]);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save progress.");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- DELETE ENTRY ---------------- */
  const handleDelete = async (log) => {
    if ((log.pmcStatus || "PENDING") === "APPROVED") {
      toast.error("Cannot delete – PMC already approved.");
      return;
    }

    const ok = window.confirm("Delete this entry?");
    if (!ok) return;

    try {
      await deleteDoc(
        doc(db, "tenders", tenderId, "construction_logs", log.id)
      );
      setLogs((p) => p.filter((x) => x.id !== log.id));
      toast.success("Entry deleted.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete.");
    }
  };

  /* ---------------- RENDER ---------------- */

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-emerald-50">
        <Toaster />
        <div className="text-sm text-slate-600">Loading…</div>
      </div>
    );
  }

  if (!tender) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-emerald-50">
        <Toaster />
        <div className="text-sm text-slate-600">Tender not found.</div>
      </div>
    );
  }

  const stageOptions = STAGES;

  return (
    <div className="min-h-screen bg-emerald-50 p-6">
      <Toaster />

      {/* HEADER */}
      <div className="max-w-6xl mx-auto mb-4">
        <div className="rounded-2xl bg-white shadow border border-emerald-200 px-5 py-4 flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] text-slate-500 font-semibold uppercase tracking-[0.18em]">
              Daily Progress – BOQ Item wise
            </div>
            <div className="mt-1 text-lg font-bold text-emerald-800">
              {tender.title || tender.projectName || "Construction Project"}
            </div>
            <div className="mt-1 text-[11px] text-slate-600">
              Contractor (You):{" "}
              <b>{vendor?.profile?.companyName || "Your Company"}</b>
            </div>
          </div>
          <div className="text-right text-[11px] text-slate-600 space-y-1">
            <div>
              <b>Date:</b> {dayjs().format("DD/MM/YYYY")}
            </div>
            <div>
              <b>Tender ID:</b> {tender.id}
            </div>
            <button
              onClick={() => navigate("/tender/contractor")}
              className="mt-2 text-emerald-700 underline"
            >
              ← Back to Contractor Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-4">
        {/* FORM */}
        <div className="md:col-span-1 bg-white rounded-2xl shadow border border-emerald-100 p-4">
          <h3 className="text-sm font-semibold text-emerald-800 mb-2">
            Add Progress Entry
          </h3>

          <div className="space-y-3 text-[12px]">
            {/* Construction Stage */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-600">
                Construction Stage
              </label>
              <select
                value={stageKey}
                onChange={(e) => setStageKey(e.target.value)}
                className="mt-1 w-full border rounded-lg px-3 py-1.5"
              >
                {stageOptions.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 🔹 NEW: BOQ Item select */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-600">
                BOQ Item (link progress to item)
              </label>
              {boqItems.length === 0 ? (
                <div className="mt-1 text-[11px] text-slate-500">
                  BOQ items not available.
                </div>
              ) : (
                <select
                  value={selectedBoqId}
                  onChange={(e) => setSelectedBoqId(e.target.value)}
                  className="mt-1 w-full border rounded-lg px-3 py-1.5"
                >
                  {boqItems.map((b) => (
                    <option key={b.id} value={b.id}>
                      #{b.itemNo || "-"} – {b.unit || ""} –{" "}
                      {(b.description || "").slice(0, 40)}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-600">
                Qty Executed Today
              </label>
              <input
                type="number"
                value={qtyToday}
                onChange={(e) => setQtyToday(e.target.value)}
                className="mt-1 w-full border rounded-lg px-3 py-1.5"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-600">
                Remarks
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={3}
                className="mt-1 w-full border rounded-lg px-3 py-1.5 resize-y"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-600">
                Photos (optional)
              </label>
              <input
                type="file"
                multiple
                onChange={(e) => setPhotos([...e.target.files])}
                className="mt-1 text-[11px]"
              />
              {photos.length > 0 && (
                <div className="mt-1 text-[10px] text-slate-500">
                  {photos.length} file(s) selected.
                </div>
              )}
            </div>

            <div className="pt-1">
              <button
                onClick={handleSave}
                disabled={saving}
                className={cx(
                  "w-full rounded-lg text-[12px] font-semibold text-white py-1.5",
                  saving
                    ? "bg-emerald-300 cursor-not-allowed"
                    : "bg-emerald-700 hover:bg-emerald-800"
                )}
              >
                {saving ? "Saving…" : "Submit Progress"}
              </button>
            </div>

            <div className="text-[10px] text-slate-500 mt-1">
              All entries go to PMC for approval. Once PMC marks entry as
              APPROVED, you will not be able to delete it.
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="md:col-span-2 bg-white rounded-2xl shadow border border-emerald-100 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-emerald-800">
              Progress History
            </h3>
            <div className="text-[10px] text-slate-500">
              PMC Status: <Badge color="yellow">PENDING</Badge>,{" "}
              <Badge color="green">APPROVED</Badge>,{" "}
              <Badge color="red">REJECTED</Badge>
            </div>
          </div>

          {logs.length === 0 ? (
            <div className="text-[12px] text-slate-500">
              No progress entries yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-[12px] border border-slate-200">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="border px-2 py-1 text-left w-24">Date</th>
                    <th className="border px-2 py-1 text-left w-40">
                      Stage
                    </th>
                    <th className="border px-2 py-1 text-left w-40">
                      BOQ Item
                    </th>
                    <th className="border px-2 py-1 text-right w-16">
                      Qty
                    </th>
                    <th className="border px-2 py-1 text-center w-16">
                      Photos
                    </th>
                    <th className="border px-2 py-1 text-left w-28">
                      PMC Status
                    </th>
                    <th className="border px-2 py-1 text-left">
                      Remarks / PMC Remark
                    </th>
                    <th className="border px-2 py-1 text-center w-24">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const d = safeDate(log.date);
                    const dateStr = d ? dayjs(d).format("DD/MM/YYYY") : "";
                    const stage = STAGES.find(
                      (s) => s.key === log.stageKey
                    );
                    const pmcStatus = (log.pmcStatus || "PENDING").toUpperCase();

                    let color = "yellow";
                    if (pmcStatus === "APPROVED") color = "green";
                    else if (pmcStatus === "REJECTED") color = "red";

                    return (
                      <tr key={log.id} className="border-b">
                        <td className="border px-2 py-1 align-top">
                          {dateStr}
                        </td>
                        <td className="border px-2 py-1 align-top">
                          {stage?.label || log.stageKey}
                        </td>
                        {/* 🔹 BOQ item info in history */}
                        <td className="border px-2 py-1 align-top">
                          {log.boqItemNo ? `#${log.boqItemNo}` : "—"}{" "}
                          {log.boqDescription
                            ? "– " +
                              String(log.boqDescription).slice(0, 35)
                            : ""}
                        </td>
                        <td className="border px-2 py-1 align-top text-right">
                          {log.qtyToday}
                        </td>
                        <td className="border px-2 py-1 align-top text-center">
                          {log.photos?.length || 0}
                        </td>
                        <td className="border px-2 py-1 align-top">
                          <Badge color={color}>{pmcStatus}</Badge>
                        </td>
                        <td className="border px-2 py-1 align-top">
                          {log.remarks && (
                            <div>
                              <span className="font-semibold">
                                You:
                              </span>{" "}
                              {log.remarks}
                            </div>
                          )}
                          {log.pmcRemark && (
                            <div className="mt-0.5">
                              <span className="font-semibold">
                                PMC:
                              </span>{" "}
                              {log.pmcRemark}
                            </div>
                          )}
                          {!log.remarks && !log.pmcRemark && (
                            <span className="text-[10px] text-slate-400">
                              — No remarks —
                            </span>
                          )}
                        </td>
                        <td className="border px-2 py-1 align-top text-center">
                          {pmcStatus !== "APPROVED" ? (
                            <button
                              onClick={() => handleDelete(log)}
                              className="bg-red-500 text-white text-[11px] px-2 py-1 rounded"
                            >
                              Delete
                            </button>
                          ) : (
                            <span className="text-[10px] text-slate-400">
                              Locked
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
